import React, { useEffect } from 'react';
import {
  Breakpoints,
  HBox,
  intl,
  Maskable,
  TextInput,
  Toggle,
  useBreakpoint,
  useManagedForm,
  yup
} from 'ehs-react';
import { usePortletStore } from '../App';
import shallow from 'zustand/shallow';
import { useAccessStore } from '../store/access.store';
import { AccessPageLogo } from './AccessPage/AccessPageLogo';
import { Imprint } from './AccessPage/Imprint';
import { CustomErrorMessage } from './AccessPage/CustomErrorMessage';
import { Header } from './AccessPage/Header';
import type { at } from '../generated/Beans';

type AccessInformation = at.sense.portlets.saf.checkaccess.beans.ui.AccessInformation;

export function AccessPage(): React.JSX.Element {
  const [url, accessCode] = usePortletStore(
    state => [state.url, state.configuration.accessCode],
    shallow
  );

  const [checkAccess, loading] = useAccessStore(
    state => [state.checkAccess, state.loading],
    shallow
  );

  const managedForm = useManagedForm<AccessInformation>({
    initialValues: {
      accessCode: accessCode ?? '',
      pin: '',
      professional: false
    },
    validationSchema: yup
      .object()
      .shape({
        accessCode: yup.string().required(intl('ext.field.requiredMessage')),
        pin: yup.string().required(intl('ext.field.requiredMessage')),
        professional: yup.boolean().required()
      })
      .defined(),
    isInitialValid: false,
    async onSubmit(values): Promise<void> {
      checkAccess(url, values.accessCode, values.pin, values.professional);
    }
  });

  const isMobile = useBreakpoint(Breakpoints.MOBILE);

  // remove role main from outer layer, as it is used inside the form
  useEffect(() => {
    document?.querySelector('#main-content')?.removeAttribute('role');
    document
      ?.querySelector('nav.lfr-product-menu-panel')
      ?.setAttribute('aria-hidden', 'true');
  }, []);

  return (
    <HBox align="center" wrap="nowrap" className="hbox-container">
      <div className="hbox-child viewer-column">
        <AccessPageLogo />
        <form
          className="main-container"
          {...managedForm.getFormProps()}
          role="main"
        >
          <Header />
          <Maskable masked={loading}>
            <TextInput
              type="text"
              label={intl('common.access.code.capitalized')}
              labelOnTop
              {...managedForm.getField('accessCode')}
            />
            <TextInput
              type="password"
              label={intl('common.pin.capitalized')}
              labelOnTop
              {...managedForm.getField('pin')}
            />
            <CustomErrorMessage />
            <div className="action-container">
              {!isMobile && (
                <Toggle
                  label={intl('checkaccessportlet.view.professionaltoggle')}
                  {...managedForm.getField('professional')}
                />
              )}
              {managedForm.getSubmitButton({
                disableIfNotValid: false,
                disableIfNotDirty: false,
                text: intl('checkaccessportlet.view.submit')
              })}
            </div>
          </Maskable>
        </form>
        <Imprint />
      </div>

      <div className="hbox-child image-column" />
    </HBox>
  );
}
